import React, { useEffect, useMemo, useState } from "react";
import { Button, Input } from "@headlessui/react";
import {
  ArrowPathIcon,
  BuildingStorefrontIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  Squares2X2Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { db } from "../../firebase/firebase";
import { useAuth } from "../../contexts/authContext";
import { useDarkMode } from "../../contexts/darkModeContext";
import { salesCodeMapping } from "../GLOBAL/GlobalFunctions";
import { getStoredJSON, setStoredJSON } from "../GLOBAL/storageUtils";
import { getAllGeocodedLocations } from "./GeocodeUtils";
import MapComponent from "./MapComponent";
import logo from "../../PFC_Images/main-logo.png";

const TERRITORY_COLORS = [
  "#047857",
  "#2563eb",
  "#ca8a04",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
  "#ea580c",
  "#4d7c0f",
];

const pointKey = (point) => `${point.lng}:${point.lat}`;

const toRadians = (degrees) => degrees * (Math.PI / 180);

const distanceMiles = (a, b) => {
  const earthRadiusMiles = 3958.8;
  const deltaLat = toRadians(b.lat - a.lat);
  const deltaLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const haversine =
    Math.sin(deltaLat / 2) ** 2 +
    Math.sin(deltaLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);

  return 2 * earthRadiusMiles * Math.asin(Math.sqrt(haversine));
};

const median = (values) => {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);

  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

const getClusterCenter = (points) => ({
  lat: median(points.map((point) => point.lat)),
  lng: median(points.map((point) => point.lng)),
});

const removeTerritoryOutliers = (points) => {
  if (points.length < 5) return { corePoints: points, outlierCount: 0 };

  const center = getClusterCenter(points);
  const distances = points.map((point) => distanceMiles(point, center));
  const medianDistance = median(distances);
  const deviations = distances.map((distance) => Math.abs(distance - medianDistance));
  const medianDeviation = median(deviations);
  const threshold = Math.max(medianDistance + medianDeviation * 3.5, medianDistance * 2.25, 35);

  const corePoints = points.filter((point, index) => distances[index] <= threshold);

  if (corePoints.length < 3) {
    return { corePoints: points, outlierCount: 0 };
  }

  return {
    corePoints,
    outlierCount: points.length - corePoints.length,
  };
};

const cross = (origin, a, b) =>
  (a.lng - origin.lng) * (b.lat - origin.lat) - (a.lat - origin.lat) * (b.lng - origin.lng);

const convexHull = (points) => {
  const uniquePoints = Array.from(new Map(points.map((point) => [pointKey(point), point])).values())
    .sort((a, b) => (a.lng === b.lng ? a.lat - b.lat : a.lng - b.lng));

  if (uniquePoints.length <= 1) return uniquePoints;

  const lower = [];
  uniquePoints.forEach((point) => {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], point) <= 0) {
      lower.pop();
    }
    lower.push(point);
  });

  const upper = [];
  [...uniquePoints].reverse().forEach((point) => {
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], point) <= 0) {
      upper.pop();
    }
    upper.push(point);
  });

  return lower.slice(0, -1).concat(upper.slice(0, -1));
};

const paddedBoundary = (points) => {
  const lats = points.map((point) => point.lat);
  const lngs = points.map((point) => point.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const latPad = Math.max((maxLat - minLat) * 0.25, 0.12);
  const lngPad = Math.max((maxLng - minLng) * 0.25, 0.12);

  return [
    { lat: minLat - latPad, lng: minLng - lngPad },
    { lat: minLat - latPad, lng: maxLng + lngPad },
    { lat: maxLat + latPad, lng: maxLng + lngPad },
    { lat: maxLat + latPad, lng: minLng - lngPad },
  ];
};

const getRepBoundary = (points) => {
  if (points.length < 3) return paddedBoundary(points);
  const hull = convexHull(points);
  return hull.length >= 3 ? hull : paddedBoundary(points);
};

const smoothClosedPath = (path, iterations = 2) => {
  if (path.length < 3) return path;

  let smoothedPath = path;

  for (let i = 0; i < iterations; i += 1) {
    const sourcePath = smoothedPath;
    const nextPath = [];

    for (let index = 0; index < sourcePath.length; index += 1) {
      const point = sourcePath[index];
      const nextPoint = sourcePath[(index + 1) % sourcePath.length];
      nextPath.push({
        lat: point.lat * 0.75 + nextPoint.lat * 0.25,
        lng: point.lng * 0.75 + nextPoint.lng * 0.25,
      });
      nextPath.push({
        lat: point.lat * 0.25 + nextPoint.lat * 0.75,
        lng: point.lng * 0.25 + nextPoint.lng * 0.75,
      });
    }

    smoothedPath = nextPath;
  }

  return smoothedPath;
};

const getPathBounds = (path) => {
  const lats = path.map((point) => point.lat);
  const lngs = path.map((point) => point.lng);

  return {
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
    minLng: Math.min(...lngs),
    maxLng: Math.max(...lngs),
  };
};

const boundsOverlap = (a, b) =>
  a.minLat <= b.maxLat &&
  a.maxLat >= b.minLat &&
  a.minLng <= b.maxLng &&
  a.maxLng >= b.minLng;

const isPointInPolygon = (point, polygon) => {
  let isInside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const current = polygon[i];
    const previous = polygon[j];
    const intersects =
      (current.lat > point.lat) !== (previous.lat > point.lat) &&
      point.lng < ((previous.lng - current.lng) * (point.lat - current.lat)) / (previous.lat - current.lat) + current.lng;

    if (intersects) isInside = !isInside;
  }

  return isInside;
};

const getSegmentOrientation = (a, b, c) => {
  const value = (b.lat - a.lat) * (c.lng - b.lng) - (b.lng - a.lng) * (c.lat - b.lat);
  if (Math.abs(value) < 0.0000001) return 0;
  return value > 0 ? 1 : 2;
};

const isPointOnSegment = (a, b, c) =>
  b.lng <= Math.max(a.lng, c.lng) &&
  b.lng >= Math.min(a.lng, c.lng) &&
  b.lat <= Math.max(a.lat, c.lat) &&
  b.lat >= Math.min(a.lat, c.lat);

const doSegmentsIntersect = (a, b, c, d) => {
  const first = getSegmentOrientation(a, b, c);
  const second = getSegmentOrientation(a, b, d);
  const third = getSegmentOrientation(c, d, a);
  const fourth = getSegmentOrientation(c, d, b);

  if (first !== second && third !== fourth) return true;
  if (first === 0 && isPointOnSegment(a, c, b)) return true;
  if (second === 0 && isPointOnSegment(a, d, b)) return true;
  if (third === 0 && isPointOnSegment(c, a, d)) return true;
  if (fourth === 0 && isPointOnSegment(c, b, d)) return true;

  return false;
};

const getPathSegments = (path) =>
  path.map((point, index) => [point, path[(index + 1) % path.length]]);

const doTerritoriesOverlap = (firstPath, secondPath) => {
  if (firstPath.length < 3 || secondPath.length < 3) return false;
  if (!boundsOverlap(getPathBounds(firstPath), getPathBounds(secondPath))) return false;
  if (firstPath.some((point) => isPointInPolygon(point, secondPath))) return true;
  if (secondPath.some((point) => isPointInPolygon(point, firstPath))) return true;

  const firstSegments = getPathSegments(firstPath);
  const secondSegments = getPathSegments(secondPath);

  return firstSegments.some(([a, b]) =>
    secondSegments.some(([c, d]) => doSegmentsIntersect(a, b, c, d))
  );
};

const CustomerMap = () => {
  const { darkMode } = useDarkMode();
  const { currentUser } = useAuth();

  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [geocodedAddresses, setGeocodedAddresses] = useState([]);
  const [selectedSalesCodes, setSelectedSalesCodes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("pins");
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingMap, setLoadingMap] = useState(false);
  const [mapRefreshKey, setMapRefreshKey] = useState(0);
  const [accountLevel, setAccountLevel] = useState("");
  const [selectedTerritoryCode, setSelectedTerritoryCode] = useState(null);
  const [expandedOverlapCode, setExpandedOverlapCode] = useState(null);

  const activeCacheOptions = useMemo(() => ({
    maxBytes: 650000,
    evictPrefixes: [
      "allActiveCustomers",
      `customerAddresses_${currentUser?.uid || ""}`,
      `activeCustomers_${currentUser?.uid || ""}`,
      `plannedOrders_${currentUser?.uid || ""}`,
    ],
  }), [currentUser?.uid]);

  useEffect(() => {
    const fetchUserLevel = async () => {
      if (!currentUser) return;

      try {
        const storedUserData = getStoredJSON(`userData_${currentUser.uid}`);
        if (storedUserData) {
          setAccountLevel(storedUserData.accountLevel);
          return;
        }

        const userDoc = await db.collection("Users").doc(currentUser.uid).get();
        if (userDoc.exists) {
          const userData = userDoc.data();
          setAccountLevel(userData.accountLevel);
          setStoredJSON(`userData_${currentUser.uid}`, userData, { maxBytes: 100000 });
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserLevel();
  }, [currentUser]);

  useEffect(() => {
    const fetchCustomersForAll = async () => {
      setLoadingCustomers(true);
      try {
        const usersSnapshot = await db.collection("Users")
          .where("accountLevel", "in", ["S", "A"])
          .get();

        let allCustomers = [];

        for (const userDoc of usersSnapshot.docs) {
          const userId = userDoc.id;
          const customerSnapshot = await db
            .collection("Customers")
            .doc(userId)
            .collection("clients")
            .get();

          const userCustomers = customerSnapshot.docs.map((doc) => ({
            id: doc.id,
            salesID: userId,
            ...doc.data(),
          }));

          allCustomers = [...allCustomers, ...userCustomers];
        }

        setCustomers(allCustomers);
      } catch (error) {
        console.error("Error fetching all active customers:", error);
      } finally {
        setLoadingCustomers(false);
      }
    };

    const fetchCustomersActive = async () => {
      if (!currentUser) return;

      setLoadingCustomers(true);
      try {
        const storedActiveData = getStoredJSON(`activeCustomers_${currentUser.uid}`);
        if (storedActiveData) {
          setCustomers(storedActiveData);
          return;
        }

        const snapshot = await db
          .collection("Customers")
          .doc(currentUser.uid)
          .collection("clients")
          .get();

        const activeData = snapshot.docs.map((doc) => ({
          id: doc.id,
          salesID: currentUser.uid,
          ...doc.data(),
        }));

        setCustomers(activeData);
        setStoredJSON(`activeCustomers_${currentUser.uid}`, activeData, activeCacheOptions);
      } catch (error) {
        console.error("Error fetching active customers data from Firestore:", error);
      } finally {
        setLoadingCustomers(false);
      }
    };

    if (accountLevel === "D") {
      fetchCustomersForAll();
    } else {
      fetchCustomersActive();
    }
  }, [currentUser, accountLevel, activeCacheOptions]);

  const salesCodes = useMemo(
    () => [...new Set(customers.map((customer) => customer.salesCode).filter(Boolean))].sort(),
    [customers]
  );

  useEffect(() => {
    setSelectedSalesCodes(salesCodes);
  }, [salesCodes]);

  useEffect(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const filtered = customers.filter((customer) => {
      const matchesSalesCode = selectedSalesCodes.includes(customer.salesCode);
      const matchesSearch =
        !normalizedQuery ||
        [customer.name, customer.customerCode, customer.city, customer.state, customer.salesCode]
          .filter(Boolean)
          .some((value) => value.toString().toLowerCase().includes(normalizedQuery));

      return matchesSalesCode && matchesSearch;
    });

    setFilteredCustomers(filtered);
  }, [customers, searchQuery, selectedSalesCodes]);

  useEffect(() => {
    const fetchGeocodedData = async () => {
      setLoadingMap(true);
      try {
        const geocodes = await getAllGeocodedLocations(filteredCustomers);
        setGeocodedAddresses(geocodes);
      } finally {
        setLoadingMap(false);
      }
    };

    if (filteredCustomers.length > 0) {
      fetchGeocodedData();
    } else {
      setGeocodedAddresses([]);
    }
  }, [filteredCustomers, mapRefreshKey]);

  const territoryPolygons = useMemo(() => {
    const grouped = {};

    geocodedAddresses.forEach((location) => {
      if (!location.repCode || !location.lat || !location.lng) return;
      if (!grouped[location.repCode]) grouped[location.repCode] = [];
      grouped[location.repCode].push(location);
    });

    return Object.entries(grouped).map(([salesCode, locations], index) => {
      const points = locations.map((location) => ({ lat: location.lat, lng: location.lng }));
      const { corePoints, outlierCount } = removeTerritoryOutliers(points);
      const corePointKeys = new Set(corePoints.map(pointKey));
      const territoryLocations = locations.filter((location) =>
        corePointKeys.has(pointKey({ lat: location.lat, lng: location.lng }))
      );
      const boundary = getRepBoundary(corePoints);

      return {
        salesCode,
        name: salesCodeMapping[salesCode] || salesCode,
        color: TERRITORY_COLORS[index % TERRITORY_COLORS.length],
        path: smoothClosedPath(boundary),
        locations: territoryLocations,
        points: points.length,
        corePoints: corePoints.length,
        outlierCount,
      };
    });
  }, [geocodedAddresses]);

  const repSummaries = useMemo(() => {
    const summaries = {};

    customers.forEach((customer) => {
      if (!customer.salesCode) return;
      if (!summaries[customer.salesCode]) {
        summaries[customer.salesCode] = { salesCode: customer.salesCode, total: 0, visible: 0, mapped: 0, outlierCount: 0 };
      }
      summaries[customer.salesCode].total += 1;
    });

    filteredCustomers.forEach((customer) => {
      if (!customer.salesCode || !summaries[customer.salesCode]) return;
      summaries[customer.salesCode].visible += 1;
    });

    geocodedAddresses.forEach((location) => {
      if (!location.repCode || !summaries[location.repCode]) return;
      summaries[location.repCode].mapped += 1;
    });

    territoryPolygons.forEach((territory) => {
      if (!summaries[territory.salesCode]) return;
      summaries[territory.salesCode].outlierCount = territory.outlierCount;
    });

    return Object.values(summaries).sort((a, b) => b.visible - a.visible);
  }, [customers, filteredCustomers, geocodedAddresses, territoryPolygons]);

  const selectedTerritory = useMemo(
    () => territoryPolygons.find((territory) => territory.salesCode === selectedTerritoryCode),
    [selectedTerritoryCode, territoryPolygons]
  );

  const territoryOverlaps = useMemo(() => {
    const overlaps = {};

    territoryPolygons.forEach((territory) => {
      overlaps[territory.salesCode] = [];
    });

    for (let i = 0; i < territoryPolygons.length; i += 1) {
      for (let j = i + 1; j < territoryPolygons.length; j += 1) {
        const first = territoryPolygons[i];
        const second = territoryPolygons[j];

        if (!doTerritoriesOverlap(first.path, second.path)) continue;

        overlaps[first.salesCode].push({
          salesCode: second.salesCode,
          name: second.name,
          color: second.color,
          pins: second.locations.length,
        });
        overlaps[second.salesCode].push({
          salesCode: first.salesCode,
          name: first.name,
          color: first.color,
          pins: first.locations.length,
        });
      }
    }

    Object.keys(overlaps).forEach((salesCode) => {
      overlaps[salesCode].sort((a, b) => a.name.localeCompare(b.name));
    });

    return overlaps;
  }, [territoryPolygons]);

  const selectOnlyRep = (salesCode) => {
    setSelectedSalesCodes([salesCode]);
    setSelectedTerritoryCode(salesCode);
    setViewMode("territory");
  };

  const toggleRep = (salesCode) => {
    setSelectedSalesCodes((current) =>
      current.includes(salesCode)
        ? current.filter((code) => code !== salesCode)
        : [...current, salesCode]
    );
  };

  const resetFilters = () => {
    setSearchQuery("");
    setSelectedSalesCodes(salesCodes);
    setSelectedTerritoryCode(null);
  };

  return (
    <div className={`${darkMode ? "bg-neutral-950 text-white" : "bg-neutral-100 text-neutral-950"} h-[calc(100dvh-3.5rem)] w-full overflow-hidden`}>
      <div className="flex h-full w-full animate-fadeIn">
          
          {/*SideBAr*/}
        <aside className={`${darkMode ? "bg-darkMainColor border-neutral-800" : "bg-white border-neutral-200"} z-40 flex h-full w-64 shrink-0 flex-col overflow-hidden border-r sm:w-72`}>
          
          
          <div className="flex h-14 shrink-0 items-center gap-3 border-b border-neutral-200/70 px-2 dark:border-neutral-800">
            <div className={`flex size-10 items-center justify-center rounded-lg ${darkMode ? 'bg-white/5' : 'bg-black/5'}`}>
              <img src={logo} alt="" className="size-6 object-contain" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold">Rep Territories / Map</div>
              <div className="text-xs text-neutral-500">
                {loadingCustomers ? "Loading accounts..." : `${filteredCustomers.length} visible customers`} · satellite
              </div>
            </div>
          </div>

          <div className="flex-1 space-y-2 overflow-y-auto px-3 pb-3 scrollbar-hide">
            
            
            <div className={`hidden ${darkMode ? "bg-neutral-900 ring-neutral-800" : "bg-white ring-neutral-200"} flex items-center gap-2 rounded-md px-2.5 py-2 text-sm ring-1 ring-inset`}>
              <MagnifyingGlassIcon className="size-4 text-neutral-400" />
              <Input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search customer, city, rep..."
                className="w-full bg-transparent text-sm outline-none placeholder:text-neutral-400"
              />
              {searchQuery && (
                <button type="button" onClick={() => setSearchQuery("")}>
                  <XMarkIcon className="size-4 text-neutral-400" />
                </button>
              )}
            </div>

            <div className={`grid grid-cols-2 rounded-md p-1 ${darkMode ? "bg-white/5" : "bg-black/5"}`}>
              {[
                { id: "pins", label: "Pins", icon: MapPinIcon },
                { id: "territory", label: "Territory", icon: Squares2X2Icon },
              ].map((mode) => {
                const Icon = mode.icon;
                const isActive = viewMode === mode.id;

                return (
                  <Button
                    type="button"
                    key={mode.id}
                    onClick={() => setViewMode(mode.id)}
                    className={`flex h-6 items-center justify-center gap-1.5 rounded-lg text-xs font-semibold transition ${isActive ? "bg-green-600 text-white shadow-sm" : "text-neutral-500 hover:text-emerald-900"}`}
                  >
                    <Icon className="size-4" />
                    {mode.label}
                  </Button>
                );
              })}
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className={`rounded-lg px-2 py-1 ${darkMode ? "bg-white/5" : "bg-black/5"}`}>
                <div className="text-xs text-neutral-500">Reps</div>
                <div className="text-lg font-bold">{selectedSalesCodes.length}</div>
              </div>
              <div className={`rounded-lg px-2 py-1 ${darkMode ? "bg-white/5" : "bg-black/5"}`}>
                <div className="text-xs text-neutral-500">Pins</div>
                <div className="text-lg font-bold">{geocodedAddresses.length}</div>
              </div>
              <div className={`rounded-lg px-2 py-1 ${darkMode ? "bg-white/5" : "bg-black/5"}`}>
                <div className="text-xs text-neutral-500">Areas</div>
                <div className="text-lg font-bold">{territoryPolygons.length}</div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setSelectedSalesCodes(salesCodes);
                  setSelectedTerritoryCode(null);
                }}
                className="h-7 flex-1 rounded-lg bg-green-600 text-xs font-semibold text-white hover:bg-opacity-50 duration-500"
              >
                All Reps
              </Button>
              <Button onClick={resetFilters} className={`h-7 flex-1 rounded-md text-xs font-semibold ${darkMode ? 'bg-white/5' : 'bg-black/5'} hover:bg-opacity-50 duration-500`}>
                Reset
              </Button>
            </div>

            <Button
              onClick={() => setMapRefreshKey((key) => key + 1)}
              className={`flex h-7 w-full items-center justify-center gap-2 rounded-md text-xs font-semibold bg-blue-600 text-white hover:bg-opacity-50 duration-500`}
            >
              <ArrowPathIcon className={`size-4 ${loadingMap ? "animate-spin" : ""}`} />
              Refresh Map Data
            </Button>

            {viewMode === "territory" && selectedTerritory && (
              <div
                className={`rounded-lg p-2 ${darkMode ? "bg-white/5" : "bg-black/5"}`}
              >
                <div className="mb-2 flex items-center gap-2">
                  <span
                    className="size-2.5 rounded-full"
                    style={{ backgroundColor: selectedTerritory.color }}
                  />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-bold">{selectedTerritory.name}</div>
                    <div className="text-xs text-neutral-500">[{selectedTerritory.salesCode}] selected territory</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className={`rounded-lg p-2 ${darkMode ? "bg-darkMainColor" : "bg-white"}`}>
                    <div className="font-bold">{selectedTerritory.locations.length}</div>
                    <div className="text-neutral-500">Pins shown</div>
                  </div>
                  <div className={`rounded-lg p-2 ${darkMode ? "bg-darkMainColor" : "bg-white"}`}>
                    <div className="font-bold">{selectedTerritory.outlierCount}</div>
                    <div className="text-neutral-500">Outliers ignored</div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <div className="mb-2 flex items-center justify-between text-xs font-bold uppercase tracking-wide text-neutral-500">
                <span>Sales Reps</span>
                {loadingMap && <ArrowPathIcon className="size-4 animate-spin text-emerald-900" />}
              </div>

              <div className="space-y-1.5">
                {repSummaries.map((rep, index) => {
                  const isSelected = selectedSalesCodes.includes(rep.salesCode);
                  const color = TERRITORY_COLORS[index % TERRITORY_COLORS.length];
                  const overlaps = territoryOverlaps[rep.salesCode] || [];
                  const isOverlapExpanded = expandedOverlapCode === rep.salesCode;

                  return (
                    <div
                      key={rep.salesCode}
                      className={`rounded-md px-1 transition ${isSelected ? "border-2 border-green-600 bg-green-600/5" : darkMode ? "border-neutral-800 bg-white/5" : "border-neutral-200 bg-black/5"}`}
                    >
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          onClick={() => toggleRep(rep.salesCode)}
                          className="size-5 rounded-md"
                          style={{ backgroundColor: isSelected ? color : "#d4d4d4" }}
                          aria-label={`Toggle ${rep.salesCode}`}
                        />
                        <Button
                          type="button"
                          onClick={() => selectOnlyRep(rep.salesCode)}
                          className="min-w-0 flex-1 text-left"
                        >
                          <div className="truncate text-sm font-semibold">{salesCodeMapping[rep.salesCode] || rep.salesCode}</div>
                          <div className="text-xs text-neutral-500">
                            {rep.visible} visible | {rep.mapped} mapped
                            {/*{rep.outlierCount ? ` · ${rep.outlierCount} outlier${rep.outlierCount === 1 ? "" : "s"} ignored` : ""}*/}
                          </div>
                        </Button>
                        
                        {/*<BuildingStorefrontIcon className="size-4 text-neutral-400" />*/}
                          
                          <div className={`text-white size-6 rounded-lg flex items-center justify-center`} style={{ backgroundColor: isSelected ? color : "#d4d4d4" }}>
                              <span className={`text-xs font-bold italic`}>{rep.salesCode}</span>
                          </div>
                          
                      </div>

                      <div className="border-t border-neutral-200/70 py-1 dark:border-neutral-800">
                        <button
                          type="button"
                          onClick={() => setExpandedOverlapCode(isOverlapExpanded ? null : rep.salesCode)}
                          className="flex w-full items-center justify-between text-xs font-semibold text-neutral-500 hover:text-emerald-900"
                        >
                          <span>
                            Overlaps {overlaps.length ? `(${overlaps.length})` : ""}
                          </span>
                          <ChevronDownIcon className={`size-4 transition ${isOverlapExpanded ? "rotate-180" : ""}`} />
                        </button>

                        {isOverlapExpanded && (
                          <div className="mt-2 space-y-1">
                            {overlaps.length > 0 ? overlaps.map((overlap) => (
                              <Button
                                key={overlap.salesCode}
                                type="button"
                                onClick={() => selectOnlyRep(overlap.salesCode)}
                                className={`flex w-full items-center gap-2 rounded-lg px-2 py-1 text-left text-xs transition ${darkMode ? "bg-darkMainColor hover:bg-neutral-800" : "bg-white hover:bg-emerald-900/10"}`}
                              >
                                <span
                                  className="size-3 shrink-0 rounded-md"
                                  style={{ backgroundColor: overlap.color }}
                                />
                                <span className="flex items-center w-full">
                                  <span className="block truncate font-semibold">{overlap.name}</span>
                                  <span className="block opacity-70 italic ml-auto">[{overlap.salesCode}] {overlap.pins} pins</span>
                                </span>
                              </Button>
                            )) : (
                              <div className={`rounded px-2 py-1.5 text-xs text-neutral-500 ${darkMode ? "bg-neutral-950/60" : "bg-white/80"}`}>
                                No territory overlaps detected.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
        </aside>

          {/*Map*/}
        <main className="relative min-w-0 flex-1">
          <div className="absolute inset-0">
            <MapComponent
              disabledUI={false}
              geocodedAddresses={geocodedAddresses}
              territoryPolygons={territoryPolygons}
              viewMode={viewMode}
              selectedTerritoryCode={selectedTerritoryCode}
              onTerritorySelect={setSelectedTerritoryCode}
            />
          </div>
        </main>
        
        
      </div>
    </div>
  );
};

export default CustomerMap;
